import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { supabase } from '../../lib/supabase';
import { useCurrentCompany } from '../useCurrentCompany';

export interface TopSeller {
  variant_id: string;
  product_id: string;
  product_name: string;
  size: string | null;
  color: string | null;
  sku: string;
  units_sold: number;
  revenue_cents: number;
}

export interface RevenuePerProduct {
  product_id: string;
  product_name: string;
  units_sold: number;
  revenue_cents: number;
  variant_count: number;
}

export interface ProductFilterOption {
  id: string;
  name: string;
  variants: {
    id: string;
    sku: string;
    size: string | null;
    color: string | null;
  }[];
}

export interface SalesHistoryFilter {
  productId?: string;
  variantId?: string;
}

export interface LowStockAlert {
  variant_id: string;
  product_id: string;
  product_name: string;
  size: string | null;
  color: string | null;
  sku: string;
  location_id: string;
  quantity: number;
  units_30d: number;
  units_per_day: number;
  effective_threshold: number;
  recommended_reorder_qty: number;
  days_of_cover: number | null;
}

export interface SalesHistoryRow {
  id: string;
  created_at: string;
  variant_id: string;
  quantity_delta: number;
  product_name: string;
  size: string | null;
  color: string | null;
  sku: string;
}

export interface InventoryKpis {
  revenueCents30d: number;
  unitsSold30d: number;
  lowStockCount: number;
  topSku: string | null;
}

const STALE = 30 * 1000;

export function useTopSellers(period: '7 days' | '30 days' | '90 days' = '30 days', limit = 10) {
  const { company } = useCurrentCompany();
  return useQuery({
    queryKey: ['inventory', 'top-sellers', company?.id, period, limit],
    enabled: !!company?.id,
    staleTime: STALE,
    queryFn: async (): Promise<TopSeller[]> => {
      const { data, error } = await supabase.rpc('get_top_sellers', {
        p_company_id: company!.id,
        p_period: period,
        p_limit: limit,
      });
      if (error) throw error;
      return (data ?? []) as TopSeller[];
    },
  });
}

export function useRevenuePerProduct(
  period: '7 days' | '30 days' | '90 days' = '30 days',
  limit = 10,
) {
  const { company } = useCurrentCompany();
  return useQuery({
    queryKey: ['inventory', 'revenue-per-product', company?.id, period, limit],
    enabled: !!company?.id,
    staleTime: STALE,
    queryFn: async (): Promise<RevenuePerProduct[]> => {
      // RPC name not yet in generated types — regen yarn supabase:gen:types after migration deploy.
      // @ts-expect-error pending types regen for get_revenue_per_product (#743 follow-up)
      const { data, error } = await supabase.rpc('get_revenue_per_product', {
        p_company_id: company!.id,
        p_period: period,
        p_limit: limit,
      });
      if (error) throw error;
      return ((data ?? []) as unknown) as RevenuePerProduct[];
    },
  });
}

export function useProductsForFilter() {
  const { company } = useCurrentCompany();
  return useQuery({
    queryKey: ['inventory', 'products-filter', company?.id],
    enabled: !!company?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ProductFilterOption[]> => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, product_variants ( id, sku, size, color, is_active )')
        .eq('company_id', company!.id)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;

      return (data ?? []).map((row: any) => ({
        id: row.id,
        name: row.name,
        variants: (row.product_variants ?? [])
          .filter((v: any) => v.is_active)
          .map((v: any) => ({
            id: v.id,
            sku: v.sku,
            size: v.size ?? null,
            color: v.color ?? null,
          })),
      }));
    },
  });
}

export function useLowStockAlerts() {
  const { company } = useCurrentCompany();
  return useQuery({
    queryKey: ['inventory', 'low-stock', company?.id],
    enabled: !!company?.id,
    staleTime: STALE,
    queryFn: async (): Promise<LowStockAlert[]> => {
      const { data, error } = await supabase.rpc('get_low_stock_alerts', {
        p_company_id: company!.id,
      });
      if (error) throw error;
      return (data ?? []) as LowStockAlert[];
    },
  });
}

export function useSalesHistory(limit = 50, filter: SalesHistoryFilter = {}) {
  const { company } = useCurrentCompany();
  const { productId, variantId } = filter;
  return useQuery({
    queryKey: ['inventory', 'sales-history', company?.id, limit, productId ?? null, variantId ?? null],
    enabled: !!company?.id,
    staleTime: STALE,
    queryFn: async (): Promise<SalesHistoryRow[]> => {
      // Join inventory_transactions → product_variants → products in one call
      // via PostgREST's nested-select syntax. Filters apply at the join level
      // (product_variants.product_id) or directly on variant_id.
      let query = supabase
        .from('inventory_transactions')
        .select(`
          id,
          created_at,
          variant_id,
          quantity_delta,
          product_variants!inner (
            sku, size, color, product_id,
            products!inner ( name, company_id )
          )
        `)
        .eq('type', 'sale')
        .eq('product_variants.products.company_id', company!.id);

      if (variantId) {
        query = query.eq('variant_id', variantId);
      } else if (productId) {
        query = query.eq('product_variants.product_id', productId);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;

      return (data ?? []).map((row: any) => ({
        id: row.id,
        created_at: row.created_at,
        variant_id: row.variant_id,
        quantity_delta: row.quantity_delta,
        product_name: row.product_variants?.products?.name ?? '—',
        size: row.product_variants?.size ?? null,
        color: row.product_variants?.color ?? null,
        sku: row.product_variants?.sku ?? '',
      }));
    },
  });
}

export function useInventoryKpis() {
  const { company } = useCurrentCompany();
  const topSellers = useTopSellers('30 days', 1);
  const lowStock = useLowStockAlerts();

  const query = useQuery({
    queryKey: ['inventory', 'kpis', company?.id],
    enabled: !!company?.id,
    staleTime: STALE,
    queryFn: async (): Promise<{ revenueCents30d: number; unitsSold30d: number }> => {
      const since = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();

      const [revenueRes, unitsRes] = await Promise.all([
        supabase
          .from('payment_records')
          .select('amount')
          .eq('company_id', company!.id)
          .eq('payment_type', 'pos_payment')
          .eq('status', 'succeeded')
          .gte('paid_at', since),
        supabase
          .from('inventory_transactions')
          .select('quantity_delta')
          .eq('company_id', company!.id)
          .eq('type', 'sale')
          .gte('created_at', since),
      ]);

      if (revenueRes.error) throw revenueRes.error;
      if (unitsRes.error) throw unitsRes.error;

      // payment_records.amount is stored as dollars (numeric); convert to cents.
      const revenueCents30d = (revenueRes.data ?? []).reduce(
        (sum: number, r: { amount: number | string }) => sum + Math.round(Number(r.amount) * 100),
        0,
      );
      const unitsSold30d = (unitsRes.data ?? []).reduce(
        (sum: number, r: { quantity_delta: number }) => sum + -r.quantity_delta,
        0,
      );

      return { revenueCents30d, unitsSold30d };
    },
  });

  const top = topSellers.data?.[0];
  const data: InventoryKpis | undefined = query.data
    ? {
        revenueCents30d: query.data.revenueCents30d,
        unitsSold30d: query.data.unitsSold30d,
        lowStockCount: lowStock.data?.length ?? 0,
        topSku: top ? `${top.product_name}${top.size ? ` (${top.size})` : ''}` : null,
      }
    : undefined;

  return {
    data,
    isLoading: query.isLoading || topSellers.isLoading || lowStock.isLoading,
  };
}

/**
 * Subscribe to realtime changes on inventory tables. Debounces invalidation
 * (800 ms) to batch rapid bursts of POS sales.
 */
export function useInventoryRealtime(): void {
  const { company } = useCurrentCompany();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!company?.id) return;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const invalidate = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
      }, 800);
    };

    const channel = supabase
      .channel(`inventory-${company.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'inventory_transactions' }, invalidate)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'inventory_levels' }, invalidate)
      .subscribe();

    return () => {
      clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, [company?.id, queryClient]);
}
